import { useEffect, useRef, useState } from 'react';
import { Bot, Check, Plus, Send, Trash2, X } from 'lucide-react';
import { AssistantLauncher } from '@/components/AssistantLauncher';
import type { PeriodSpec } from '@contracts';
import { useFilters } from '@/lib/filterContext';
import { useDashboard } from '@/lib/dashboardContext';
import { renderRich } from '@/lib/markdown';
import { suggestionToConfig } from '@/lib/widgetSuggestions';
import type { AssistantConfig } from '@contracts';
import { isAdmin } from '@/services/authService';
import { fetchAssistantConfig, sendChatStream, type ChatMsg, type WidgetSuggestion } from '@/services/assistantService';
import { PRESETS } from '@/services/presetQueries';
import { QuickQueries, type PresetPick } from '@/components/QuickQueries';

const STORAGE_KEY = 'portal_assistant_chat';
const WELCOME = 'Hola, soy tu asistente de negocio. Puedo consultar ingresos, pedidos, clientes, stock y más. ¿En qué te ayudo?';
const PROVIDER_FALLBACK = 'Claude (Anthropic)';

function loadMessages(): ChatMsg[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ChatMsg[];
  } catch {
    /* ignore */
  }
  return [];
}

export function AssistantPanel({ assistantCode = 'dashboard' }: { assistantCode?: string }) {
  const { period: dashPeriod, companies, view, scope } = useFilters();
  const { addWidget } = useDashboard();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>(loadMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [cleared, setCleared] = useState(false);
  const [qqOpen, setQqOpen] = useState(true);
  const [cfg, setCfg] = useState<AssistantConfig | null>(null);
  // ¿el asistente (LLM) está disponible? false = desactivado/sin config → se
  // deshabilita el texto libre, pero las consultas rápidas ($0) siguen.
  const [available, setAvailable] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* ignore */
    }
  }, [messages]);

  const loadConfig = () =>
    fetchAssistantConfig(assistantCode).then((c) => {
      setCfg(c);
      setAvailable(c !== null); // null = desactivado/sin config → no disponible
    });

  useEffect(() => {
    if (!isAdmin()) return;
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantCode]);

  if (!isAdmin()) return null;

  const welcome = cfg?.welcomeMessage || WELCOME;
  const providerLabel = cfg?.providerLabel || PROVIDER_FALLBACK;

  const scrollDown = () => setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);

  const clearChat = () => {
    setMessages([]);
    setAdded(new Set());
    setQqOpen(true);
    setCleared(true);
  };

  const addFromSuggestion = (key: string, s: WidgetSuggestion) => {
    const mapped = suggestionToConfig(s, view, dashPeriod);
    if (!mapped) return;
    addWidget(mapped.config);
    setAdded((prev) => new Set(prev).add(key));
  };

  const setAssistant = (content: string, suggestions?: WidgetSuggestion[]) =>
    setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, content, suggestions } : m)));

  // Consulta rápida precargada → respuesta DIRECTA del endpoint de métricas (sin
  // Claude, costo $0 de API). Solo el texto libre usa el modelo.
  const runPreset = async (p: PresetPick) => {
    if (loading) return;
    const base: ChatMsg[] = [...messages, { role: 'user', content: p.label }];
    setMessages([...base, { role: 'assistant', content: '' }]);
    setLoading(true);
    setQqOpen(false);
    scrollDown();
    try {
      const resolver = PRESETS[p.id];
      if (!resolver) {
        setAssistant('Esa consulta rápida no está disponible.');
        return;
      }
      const res = await resolver({ period: p.period ?? dashPeriod, companies, view, scope });
      setAssistant(res.answer, res.suggestions);
    } catch {
      setAssistant('No pude obtener ese dato ahora. Intenta de nuevo.');
    } finally {
      setLoading(false);
      scrollDown();
    }
  };

  // Texto libre → Claude (interpreta lenguaje natural). Streaming.
  const send = async (text: string, period?: PeriodSpec | null) => {
    const q = text.trim();
    if (!q || loading || !available) return;
    const base: ChatMsg[] = [...messages, { role: 'user', content: q }];
    setMessages([...base, { role: 'assistant', content: '' }]);
    setInput('');
    setLoading(true);
    setQqOpen(false);
    scrollDown();
    let acc = '';
    try {
      await sendChatStream(base, { period: period ?? dashPeriod, companies, view, scope, assistantCode }, (ev) => {
        if (ev.type === 'delta') {
          acc += ev.text;
          setAssistant(acc);
          scrollDown();
        } else if (ev.type === 'done') {
          setAssistant(acc || '(sin respuesta)', ev.suggestions);
        } else if (ev.type === 'error') {
          setAssistant(ev.message);
          // "no configurado" = asistente desactivado → marcar no disponible (banner +
          // input bloqueado). Errores transitorios (ClientError/rate limit) NO apagan.
          if (/configurad/i.test(ev.message)) setAvailable(false);
        }
      });
    } catch (e) {
      setAssistant(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
      scrollDown();
    }
  };

  const panelSide = cfg?.avatar?.position === 'bl' ? 'left-6' : 'right-6';

  return (
    <>
      <AssistantLauncher av={cfg?.avatar} assistantCode={assistantCode} open={open} onToggle={() => setOpen((o) => !o)} />

      {open && (
        <div className={`fixed bottom-24 ${panelSide} z-40 flex h-[78dvh] max-h-[700px] w-[430px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl`}>
          {/* Header (oscuro) */}
          <div className="flex items-center gap-2.5 bg-slate-900 px-4 py-3 text-white">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-orange-500">
              <Bot className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-bold leading-tight">Asistente IA</p>
              <p className="text-[11px] text-white/60">{providerLabel}</p>
            </div>
            <span title={`Modelo: ${providerLabel}`} className="rounded-lg bg-white/10 px-2 py-1 text-[11px] font-semibold text-white/80">
              IA
            </span>
            {messages.length > 0 && (
              <button onClick={clearChat} title="Limpiar conversación" className="rounded-lg p-1.5 text-white/70 hover:bg-white/10">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button onClick={() => setOpen(false)} title="Cerrar" className="rounded-lg p-1.5 text-white/70 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Mensajes */}
          <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-auto bg-slate-50 p-4">
            {/* Bienvenida (no se manda a la IA) */}
            {messages.length === 0 && (
              <>
                <WelcomeBubble text={welcome} />
                {cleared && <WelcomeBubble text="Conversación reiniciada. ¿En qué te ayudo?" />}
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {m.role === 'assistant' && (
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-800 text-white">
                    <Bot className="h-4 w-4" />
                  </span>
                )}
                <div className="max-w-[82%]">
                  <div
                    className={`whitespace-pre-wrap text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'rounded-2xl rounded-tr-sm bg-orange-500 px-3.5 py-2 text-white'
                        : 'rounded-2xl rounded-tl-sm bg-white px-3.5 py-2 text-slate-700 shadow-sm ring-1 ring-slate-100'
                    }`}
                  >
                    {m.role === 'assistant' ? renderRich(m.content) : m.content}
                  </div>
                  {m.role === 'assistant' && m.suggestions && m.suggestions.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {m.suggestions.map((s, j) => {
                        const mapped = suggestionToConfig(s, view, dashPeriod);
                        if (!mapped) return null;
                        const key = `${i}:${j}`;
                        const done = added.has(key);
                        return (
                          <button
                            key={key}
                            onClick={() => !done && addFromSuggestion(key, s)}
                            disabled={done}
                            className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition ${
                              done
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                                : 'border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100'
                            }`}
                          >
                            {done ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                            {done ? 'Agregado' : `Agregar “${mapped.label}”`}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && !messages[messages.length - 1]?.content && (
              <div className="flex items-center gap-2 text-xs italic text-slate-400">
                <span className="h-2 w-2 animate-pulse rounded-full bg-orange-400" /> Pensando…
              </div>
            )}
          </div>

          <QuickQueries onPreset={runPreset} open={qqOpen} onToggle={() => setQqOpen((o) => !o)} queries={cfg?.quickQueries} />

          {/* Aviso de no disponible (asistente desactivado/sin config). Las consultas
              rápidas ($0) siguen funcionando porque no usan el LLM. */}
          {!available && (
            <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              <span className="leading-tight">
                Asistente no disponible (desactivado o sin configurar). Las consultas rápidas siguen disponibles.
              </span>
              <button onClick={loadConfig} className="ml-auto shrink-0 font-semibold underline hover:text-amber-950">
                Reintentar
              </button>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-100 bg-white p-3">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send(input)}
                disabled={!available || loading}
                placeholder={available ? 'Escribe tu pregunta…' : 'Asistente no disponible'}
                className="flex-1 rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 disabled:cursor-not-allowed disabled:bg-slate-50"
              />
              <button
                onClick={() => send(input)}
                disabled={!available || loading}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-500 text-white transition hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-slate-400">
              <span className="font-semibold text-orange-500">{providerLabel}</span> · datos de tu CRM en tiempo real
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function WelcomeBubble({ text }: { text: string }) {
  return (
    <div className="flex gap-2">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-800 text-white">
        <Bot className="h-4 w-4" />
      </span>
      <div className="max-w-[82%] rounded-2xl rounded-tl-sm bg-white px-3.5 py-2 text-sm leading-relaxed text-slate-700 shadow-sm ring-1 ring-slate-100">
        {text}
      </div>
    </div>
  );
}
